import { useEffect, useState } from 'react'

/**
 * Tracks how much of the viewport the on-screen keyboard is covering.
 *
 * Android Chrome handles this natively via `interactive-widget=resizes-content`
 * in the viewport meta: the layout viewport shrinks, so `dvh` units already
 * react and the inset stays 0. iOS Safari ignores that directive and overlays
 * the keyboard instead, which is what this hook compensates for.
 *
 * The measurement is published as the `--keyboard-inset` custom property on
 * the root element so layout can consume it from CSS (see `.pb-keyboard`).
 */

const KEYBOARD_OPEN_THRESHOLD_PX = 120

export function useKeyboardInset(): { keyboardInset: number; isKeyboardOpen: boolean } {
  const [keyboardInset, setKeyboardInset] = useState(0)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const root = document.documentElement

    const sync = () => {
      const covered = window.innerHeight - viewport.height - viewport.offsetTop
      const inset = Math.max(0, Math.round(covered))
      root.style.setProperty('--keyboard-inset', `${inset}px`)
      setKeyboardInset(inset)
    }

    sync()
    viewport.addEventListener('resize', sync)
    viewport.addEventListener('scroll', sync)

    return () => {
      viewport.removeEventListener('resize', sync)
      viewport.removeEventListener('scroll', sync)
      root.style.removeProperty('--keyboard-inset')
    }
  }, [])

  return { keyboardInset, isKeyboardOpen: keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX }
}
